import type { ComputedRef, Ref } from "vue";
import type { ConfigField, DebugApp, DebugConfigAdapter } from "../types";
import type { DebugEnvironmentState } from "../useDebugEnvironment";
import { confirm } from "@verbleif/lib/src/composables/Dialog/confirm";
import { computed, reactive, ref, watch } from "vue";
import { toast } from "vue-sonner";
import { CONFIG_FIELDS, presetMeta, presetsFor } from "../configPresets";
import { formValuesOf, resolveEnvironmentState } from "../useDebugEnvironment";

type Translate = (key: string, named?: Record<string, unknown>) => string;

export const CUSTOM_PRESET = "custom";

export interface EnvironmentForm {
  fields: ConfigField[]
  values: Record<string, string>
  /** Errors per field; only filled once the user tried to save. */
  errors: ComputedRef<Record<string, string>>
  changedKeys: ComputedRef<string[]>
  dirty: ComputedRef<boolean>
  state: ComputedRef<DebugEnvironmentState>
  /** The preset an edit is based on: kept as `presetName` when the addresses are saved. */
  basePreset: ComputedRef<string | null>
  /** The checked tile: a preset key, `custom` while the addresses differ from a preset, or null for the default. */
  selection: Ref<string | null>
  hasOverride: ComputedRef<boolean>
  busy: Ref<boolean>
  presetLabel: (key: string) => string
  applyPreset: (key: string) => Promise<boolean>
  save: () => boolean
  resetToPreset: () => void
  resetToDefault: () => Promise<boolean>
}

function isUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * The Omgeving form of one open dialog: the addresses as edited, which differ from the saved config, and the actions
 * on the config adapter. A preset applies directly (Productie asks first); edited addresses need Opslaan and then
 * count as Aangepast, based on the preset they started from.
 */
export function createEnvironmentForm(options: {
  app: DebugApp
  config: DebugConfigAdapter
  t: Translate
  presetLabel: (key: string) => string
}): EnvironmentForm {
  const { app, config, t, presetLabel } = options;
  const fields = CONFIG_FIELDS[app];
  const presets = presetsFor(app);

  const saved = ref<Record<string, string>>(formValuesOf(app, config.config.value));
  const values = reactive<Record<string, string>>({ ...saved.value });
  const showErrors = ref(false);
  const busy = ref(false);

  const state = computed(() => resolveEnvironmentState(app, config));
  const changedKeys = computed(() => fields.filter(field => values[field.key] !== saved.value[field.key]).map(field => field.key));
  const dirty = computed(() => changedKeys.value.length > 0);
  const hasOverride = computed(() => config.overrideConfig.value !== null);

  const basePreset = computed(() => {
    const current = state.value;
    if (current.kind === "preset") {
      return current.preset;
    }
    return current.kind === "custom" ? current.basePreset : null;
  });

  function derivedSelection(): string | null {
    if (dirty.value || state.value.kind === "custom") {
      return CUSTOM_PRESET;
    }
    return state.value.kind === "preset" ? state.value.preset : null;
  }

  const selection = ref<string | null>(derivedSelection());
  watch([dirty, state], () => {
    selection.value = derivedSelection();
  });

  function fieldError(field: ConfigField): string | null {
    const value = (values[field.key] ?? "").trim();
    const label = t(field.label);
    if (value === "") {
      return t("debug_tools.environment.errors.required", { field: label });
    }
    if (field.type === "url" && !isUrl(value)) {
      return t("debug_tools.environment.errors.url", { field: label });
    }
    return null;
  }

  const allErrors = computed(() => {
    const result: Record<string, string> = {};
    for (const field of fields) {
      const error = fieldError(field);
      if (error !== null) {
        result[field.key] = error;
      }
    }
    return result;
  });
  const errors = computed(() => (showErrors.value ? allErrors.value : {}));

  function syncFromConfig() {
    saved.value = formValuesOf(app, config.config.value);
    Object.assign(values, saved.value);
    showErrors.value = false;
  }

  // The config can change outside this form (another tab, the shortcut, a preset); unsaved edits stay.
  watch(() => config.config.value, () => {
    if (!dirty.value) {
      syncFromConfig();
    }
  });

  function restoreSelection() {
    selection.value = derivedSelection();
  }

  function applyPresetValues(key: string) {
    const preset = presets[key];
    if (!preset) {
      throw new Error(`Unknown ${app} preset: ${key}`);
    }
    config.setOverrideConfig({ ...preset, presetName: key });
    syncFromConfig();
  }

  async function applyPreset(key: string): Promise<boolean> {
    if (busy.value) {
      return false;
    }
    busy.value = true;
    try {
      if (presetMeta(key).confirm) {
        const confirmed = await confirm({
          title: t("debug_tools.environment.confirm_production.title"),
          message: t("debug_tools.environment.confirm_production.message"),
          confirmText: t("debug_tools.environment.confirm_production.confirm"),
          cancelText: t("base.cancel"),
          icon: "triangle-alert",
          variant: "danger",
        });
        if (!confirmed) {
          restoreSelection();
          return false;
        }
      }
      applyPresetValues(key);
      restoreSelection();
      toast.success(t("debug_tools.environment.switched", { preset: presetLabel(key) }));
      return true;
    } finally {
      busy.value = false;
    }
  }

  function save(): boolean {
    showErrors.value = true;
    if (Object.keys(allErrors.value).length > 0) {
      return false;
    }
    const base = basePreset.value;
    const trimmed = Object.fromEntries(fields.map(field => [field.key, (values[field.key] ?? "").trim()]));
    config.setOverrideConfig({ ...(config.config.value ?? {}), ...trimmed, presetName: base });
    syncFromConfig();
    restoreSelection();
    toast.success(t("debug_tools.environment.saved"), {
      description: base
        ? t("debug_tools.environment.custom_based_on", { preset: presetLabel(base) })
        : t("debug_tools.environment.custom_own"),
    });
    return true;
  }

  function resetToPreset() {
    const base = basePreset.value;
    if (base === null) {
      throw new Error("There is no preset to restore: the addresses are not based on one.");
    }
    applyPresetValues(base);
    restoreSelection();
    toast.success(t("debug_tools.environment.restored_preset", { preset: presetLabel(base) }));
  }

  async function resetToDefault(): Promise<boolean> {
    const confirmed = await confirm({
      title: t("debug_tools.environment.confirm_reset.title"),
      message: t("debug_tools.environment.confirm_reset.message", { app: t(`debug_tools.apps.${app}`) }),
      confirmText: t("debug_tools.environment.confirm_reset.confirm"),
      cancelText: t("base.cancel"),
      icon: "triangle-alert",
      variant: "danger",
    });
    if (!confirmed) {
      return false;
    }
    config.reset();
    syncFromConfig();
    restoreSelection();
    toast.success(t("debug_tools.environment.restored_default"));
    return true;
  }

  return {
    fields,
    values,
    errors,
    changedKeys,
    dirty,
    state,
    basePreset,
    selection,
    hasOverride,
    busy,
    presetLabel,
    applyPreset,
    save,
    resetToPreset,
    resetToDefault,
  };
}
